import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { dismissHelp, undismissHelp } from '../../redux';
import { show_app_toast } from '../../services/appToast';
import IntegrationInfoAlert from '../IntegrationInfoAlert/IntegrationInfoAlert';

const has_any_dismissed_help = (dismissedHelps) => (
    Object.values(dismissedHelps || {}).some((value) => value === true)
);

/**
 * Section / page pedagogical help.
 * - dismissable (default): « Ne plus montrer »; when dismissed, a compact (i) restores it.
 * - dismissable={false}: permanent halo (same look as field HelpTooltips).
 */
const HelpInfoAlert = ({ helpId, title, children, dismissable = true }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { KUserSettings } = useSelector((state) => state.userSettings);
    const dismissed = dismissable && helpId && KUserSettings?.dismissedHelps?.[helpId] === true;

    const on_dismiss = useCallback(() => {
        const first_dismiss = !has_any_dismissed_help(KUserSettings?.dismissedHelps);
        dispatch(dismissHelp(helpId));
        if (first_dismiss) {
            show_app_toast(t('common.dismiss_help_hint'), { variant: 'info' });
        }
    }, [KUserSettings?.dismissedHelps, dispatch, helpId, t]);

    if (!helpId) {
        return null;
    }

    if (!dismissable) {
        return (
            <IntegrationInfoAlert>
                {title ? <div className="font-weight-bold mb-2">{title}</div> : null}
                {children}
            </IntegrationInfoAlert>
        );
    }

    if (dismissed) {
        return (
            <button
                type="button"
                className="btn btn-link btn-sm p-0 HelpInfoAlert-restore"
                onClick={() => dispatch(undismissHelp(helpId))}
                title={t('common.restore_help')}
                aria-label={t('common.restore_help')}
            >
                <i className="fa fa-info-circle" aria-hidden="true" />
            </button>
        );
    }

    return (
        <IntegrationInfoAlert>
            <div className="d-flex justify-content-between align-items-start w-100">
                <div className="flex-grow-1 min-w-0 mr-3">
                    {title ? <div className="font-weight-bold mb-2">{title}</div> : null}
                    {children}
                </div>
                <button
                    type="button"
                    className="alert-link bg-transparent border-0 p-0 text-nowrap"
                    onClick={on_dismiss}
                >
                    {t('common.dismiss_help')}
                </button>
            </div>
        </IntegrationInfoAlert>
    );
};

export default HelpInfoAlert;
